import RefreshIcon from "@mui/icons-material/Refresh";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { useGetAllMarketForDeliveryOffices } from "../../../../../api/services/reports/reportsApi";
import { useMediaQueries } from "../../../../../hooks/useMediaQueries";
import { useMultiSelectARIALabels } from "../../../../../hooks/useMultiSelectARIALabels";
import {
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../../helpers/utils";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../redux/slices/appState/appStateSlice";
import { reportsSearchReportsSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiBox,
  MuiButtonPrimary,
  MuiButtonTertiary,
  MuiGrid,
  MuiMultiSelect,
} from "../../../../../components";
import { SearchOutreachReportFormKeys } from "../../../modelsAndSchemas/models";
import { useGridContainer } from "../../../../../hooks/useGridContainer";
import { PendingBillingSearchFormWrapper } from "../../../ReportSearchStyle";

export interface OutreachReportFormRef {
  reset: () => void;
}
export interface OutreachReportFormProps {
  getOutreachReports: (params: { marketIds: string[] }) => void;
}
export const OutreachReportForm = forwardRef<
  OutreachReportFormRef,
  OutreachReportFormProps
>(({ getOutreachReports }, ref) => {
  const { gridSizeMedium: gridSize } = useGridContainer();
  const { fetchComponentTitle, commonLabels, useFetchLabel } = useSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const marketRef = useRef<{ resetValues: (values?: string[]) => void }>(null);

  const { isMobile } = useMediaQueries();

  const {
    isPending: isGetDeliveryOfficesPending,
    isError: isGetDeliveryOfficesError,
    data: marketList,
  } = useGetAllMarketForDeliveryOffices();

  const { reset, control, getValues } = useFormContext();
  const dispatch = useDispatch();

  useImperativeHandle(ref, () => ({
    reset: handleClearFilter,
  }));

  const setDefaultMarketValueIntoForm = () => {
    if (isNotEmpty(marketList)) {
      const marketOptions = setApiDataToDropDownOption(marketList);
      const marketValues = marketOptions.map((option) => option.value);
      reset({
        [SearchOutreachReportFormKeys.marketOfDeliveryOffices]: marketValues,
      });
      marketRef.current?.resetValues(marketValues);
    }
  };

  const marketListData = useMemo(() => {
    const selectedTemplate = isNotEmpty(marketList)
      ? setApiDataToDropDownOption(marketList).map((option) => option.value)
      : [];
    setDefaultMarketValueIntoForm();

    return {
      marketListDropdown: setApiDataToDropDownOption(marketList),
      selectedTemplate,
    };
  }, [marketList]);

  useEffect(() => {
    if (isGetDeliveryOfficesPending) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    } else {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isGetDeliveryOfficesPending]);

  useEffect(() => {
    if (isGetDeliveryOfficesError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [isGetDeliveryOfficesError]);

  const handleClearFilter = () => {
    setDefaultMarketValueIntoForm();
    const { marketOfDeliveryOffices } = getValues();
    getOutreachReports({
      marketIds: marketOfDeliveryOffices,
    });
  };

  const getLabel = useFetchLabel(reportsSearchReportsSiteCoreKeys.header);

  const renderOutReachFromHeader = () => {
    return (
      <MuiGrid container className="form-header">
        <MuiGrid justifySelf="end" className="form-header-right">
          <MuiButtonTertiary
            onClick={handleClearFilter}
            id="reset-button-outreach"
          >
            <RefreshIcon /> {commonLabels.resetAllSearch}
          </MuiButtonTertiary>
        </MuiGrid>
      </MuiGrid>
    );
  };

  const renderTitle = () => (
    <AccordionHeaderText
      label={fetchComponentTitle(reportsSearchReportsSiteCoreKeys.header)}
    />
  );
  return (
    <PendingBillingSearchFormWrapper>
      <AccordionHeader showExpandCollapseIcon={true} title={renderTitle()}>
        {renderOutReachFromHeader()}
        <MuiBox
          className="selectReportFieldContainer"
          data-testid="outreach-status-market-select"
        >
          <MuiGrid container columnSpacing={2} rowSpacing={isMobile ? 2 : 4}>
            <MuiGrid size={gridSize}>
              <FormController
                control={control}
                name={SearchOutreachReportFormKeys.marketOfDeliveryOffices}
                ref={marketRef}
              >
                <MuiMultiSelect
                  options={marketListData.marketListDropdown}
                  selectedValues={marketListData.selectedTemplate}
                  label={getLabel(
                    reportsSearchReportsSiteCoreKeys.marketForDeliveryOfficeDropDown,
                  )}
                  showSearch
                  showChip
                  id={SearchOutreachReportFormKeys.marketOfDeliveryOffices}
                  chipId={`${SearchOutreachReportFormKeys.marketOfDeliveryOffices}-chip`}
                  selectPlaceHolder={commonLabels.selectPlaceHolder}
                  searchPlaceHolder={commonLabels.search}
                  selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                  doneText={commonLabels.done}
                  allARIAProps={multiSelectARIALabels}
                  showSelectAllOption
                  isMandatory
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
        </MuiBox>
        <MuiBox className="selectReportButtonContainer" textAlign="right">
          <MuiButtonPrimary type="submit">
            {commonLabels.search}
          </MuiButtonPrimary>
        </MuiBox>
      </AccordionHeader>
    </PendingBillingSearchFormWrapper>
  );
});
